/**
 * SignIn — single-step Flow360-only login entry.
 *
 * FXC-8680 introduced the Flow360-only entry behind a must-dismiss explainer
 * modal ("Before you continue" → acknowledge → reveal the login button).
 * That interstitial made every sign-in a three-click flow and repeated
 * notices that nobody needed to act on, so it has been removed:
 *
 *   1. Page loads with "Continue with Flow360" immediately visible & active.
 *   2. The API-key and access-logging notices sit inline under the button
 *      as small print (still linked to the full privacy notice) instead of
 *      blocking the flow.
 *   3. Clicking the button calls redirectToFlow360Login(currentReturnPath())
 *      — FXC-8636 path preservation is bit-for-bit untouched.
 *
 * Google sign-in remains removed (FXC-8680). The shared auth infrastructure
 * (authClient, userManager, Keycloak OIDC callback, session handling) is
 * intentionally untouched — it is used by AuthCallback.tsx for the existing
 * OIDC code exchange and by App.tsx for session state.
 *
 * Visual language follows the redesigned public site (July 2026): dark-first,
 * 3px brand gradient rule, pilled CTAs, mono metadata — expressed through the
 * V5 design-pack tokens loaded globally from design-pack-v5/thread-tokens.css.
 */

import { Link } from "react-router-dom";
import { redirectToFlow360Login } from "../flow360/api";
import { ThreadLogo } from "../components/ThreadLogo";

// ─── helpers (FXC-8636 — DO NOT CHANGE BEHAVIOUR) ────────────────────────────

function currentAppPath(): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const fullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (base && fullPath.startsWith(`${base}/`)) {
    return fullPath.slice(base.length);
  }
  if (base && fullPath === base) {
    return "/";
  }
  return fullPath || "/";
}

function currentReturnPath(): string | undefined {
  const path = currentAppPath();
  if (path === "/sign-in" || path.startsWith("/auth/callback")) {
    return undefined;
  }
  return path;
}

function rememberPostAuthDestination(): void {
  const path = currentAppPath();
  // Never store the sign-in or callback paths themselves — the authed
  // routes block has no /sign-in route, and /auth/callback re-enters
  // this same flow. Either would render blank after sign-in.
  if (path === "/sign-in" || path.startsWith("/auth/callback")) {
    sessionStorage.removeItem("thread.post_auth_redirect");
    return;
  }
  sessionStorage.setItem("thread.post_auth_redirect", path);
}

// ─── component ───────────────────────────────────────────────────────────────

const BRAND_GRADIENT = "linear-gradient(90deg, #00643C, #61B29C, #6DB5DD)";

export default function SignIn() {
  const handleFlow360 = () => {
    rememberPostAuthDestination();
    redirectToFlow360Login(currentReturnPath());
  };

  const base = import.meta.env.BASE_URL.replace(/\/$/, "");

  // ── styles (V5 fc-* tokens; fallbacks match the dark theme) ────────────

  const pageStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#0c1110",
    fontFamily: "var(--fc-font-sans, 'TWK Everett', system-ui, sans-serif)",
    padding: 24,
    overflow: "hidden",
  };

  // Soft brand-green glow, echoing the public-site hero.
  const glowStyle: React.CSSProperties = {
    position: "absolute",
    top: -220,
    right: -160,
    width: 640,
    height: 640,
    background:
      "radial-gradient(circle, rgba(0,109,60,.18) 0%, transparent 62%)",
    pointerEvents: "none",
  };

  const cardStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
    maxWidth: 400,
    background: "#15191a",
    border: "1px solid #2a3030",
    borderRadius: 16,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const cardBodyStyle: React.CSSProperties = {
    padding: "38px 32px 30px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 26,
  };

  const primaryBtnStyle: React.CSSProperties = {
    width: "100%",
    padding: "13px 0",
    fontSize: 14.5,
    fontWeight: 600,
    fontFamily: "inherit",
    borderRadius: 999,
    cursor: "pointer",
    transition: "background 0.2s ease",
    border: "none",
    background: "#00643C",
    color: "#fff",
  };

  const ghostLinkStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    padding: "11px 0",
    textAlign: "center",
    fontSize: 13.5,
    fontWeight: 500,
    fontFamily: "inherit",
    borderRadius: 999,
    border: "1px solid color-mix(in srgb, #61B29C 40%, transparent)",
    color: "#61B29C",
    textDecoration: "none",
    transition: "background 0.2s ease",
  };

  const noticeStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 12,
    color: "#6b7570",
    lineHeight: 1.65,
    textAlign: "center",
  };

  const websiteLinkStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    padding: "8px 0 0",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 400,
    fontFamily: "inherit",
    color: "#8a948f",
    textDecoration: "none",
    transition: "color 0.15s",
  };

  return (
    <div style={pageStyle}>
      <div style={glowStyle} aria-hidden="true" />
      <div style={cardStyle}>
        {/* Signature 3px brand gradient rule */}
        <div style={{ height: 3, background: BRAND_GRADIENT }} aria-hidden="true" />

        <div style={cardBodyStyle}>
          {/* White logo + tagline */}
          <div
            style={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
          >
            <ThreadLogo height={32} showWordmark tone="white" />
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: "#8a948f",
                lineHeight: 1.5,
              }}
            >
              Provenance tracking for computational workflows
            </p>
          </div>

          {/* CTA area — one step, no interstitial */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
            }}
          >
            <button
              style={primaryBtnStyle}
              onClick={handleFlow360}
              autoFocus
              data-testid="flow360-login-btn"
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "#16B36A";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "#00643C";
              }}
            >
              Continue with Flow360
            </button>

            {/* Inline notices — informational small print, not a gate. */}
            <p style={noticeStyle}>
              <span data-testid="flow360-api-key-notice">
                Signing in stores your Flow360 API key in Thread so runs can
                be submitted and monitored on your behalf.
              </span>{" "}
              <span data-testid="privacy-logging-notice">
                Access to Thread is logged - see the{" "}
                <Link
                  to="/privacy"
                  style={{
                    color: "#61B29C",
                    textDecoration: "none",
                  }}
                >
                  Thread privacy notice
                </Link>
                .
              </span>
            </p>

            {/* FXC-8719: public example project — full read-only V5 workbench,
                no account needed. Only rendered when the deploy configures a
                public demo project id (e.g. the X-57 example). */}
            {import.meta.env.VITE_PUBLIC_DEMO_PROJECT_ID ? (
              /* FXC-8994: use a real anchor (full navigation) rather than a
                 react-router <Link>. The client-side Link rendered but did not
                 navigate on click in prod, while the same URL worked when typed
                 directly — a plain href to the same path is reliable. Auth is
                 untouched: /public/:projectId is an anonymous read-only route. */
              <a
                href={`${base}/public/${import.meta.env.VITE_PUBLIC_DEMO_PROJECT_ID}`}
                style={ghostLinkStyle}
                data-testid="public-demo-link"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "color-mix(in srgb, #61B29C 12%, transparent)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                Explore a live example project - no account needed
              </a>
            ) : null}

            {/* Secondary action — subtle link back to the Thread website.
                The static site mounts at /site/ (root, NOT under the /thread
                app basename): `${base}/site/` produced /thread/site/, a
                retired mount that the SPA catch-all swallows on the deployed
                tiers, bouncing the user back into the app. */}
            <a
              href="/site/"
              style={websiteLinkStyle}
              data-testid="view-website-link"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#e2e6e3";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#8a948f";
              }}
            >
              View Thread Website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
